import { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
  limit,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { useSnackbar } from "notistack";

/* =========================================================================
   CONSTANTS
   ========================================================================= */
const LS_KEYS = {
  MSG: (uid) => `last_seen_msg_timestamp_${uid}`,
  LIKE: (uid) => `last_seen_like_timestamp_${uid}`,
  FAV: (uid) => `last_seen_fav_timestamp_${uid}`,
};

/**
 * useRealtimeNotifications
 *
 * - userUid: auth UID of the current user (string)
 * - options.platform: optional string "browser" | "app" to force platform detection
 *
 * Behavior:
 * - Listens to user doc for live toggles: appNotifications & inBrowserNotifications
 * - Uses notifyRef to gate enqueueSnackbar calls in all listeners (so toggling doesn't force re-subscribes)
 * - Cleans up per-thread listeners when threads are removed
 * - Protects against running async callbacks after unmount
 */
export function useRealtimeNotifications({ userUid, options = {} }) {
  const { enqueueSnackbar } = useSnackbar();

  const [showNotification, setShowNotification] = useState(true); // UI-visible indicator (optional)
  const notifyRef = useRef(true); // holds current permission checked inside listeners

  // Platform detection. Default: browser if window/document exist.
  const platform =
    options.platform ||
    (typeof window !== "undefined" && typeof document !== "undefined"
      ? "browser"
      : "app");
  const isBrowser = platform === "browser";
  const isApp = platform === "app";

  // REFS
  const nameCache = useRef({});
  const threadListeners = useRef({}); // { [threadId]: unsubscribeFn }
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /* =========================================================================
     HELPER: GET USER NAME (cached)
     ========================================================================= */
  const getUserName = async (uid) => {
    if (!uid) return "Someone";
    if (nameCache.current[uid]) return nameCache.current[uid];

    try {
      const userQuery = query(
        collection(db, "users"),
        where("authUID", "==", uid)
      );

      const snapshot = await getDocs(userQuery);

      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        const name = data.displayName || "Someone";
        nameCache.current[uid] = name;
        return name;
      }
    } catch (err) {
      console.error("Name fetch error:", err);
    }

    return "Someone";
  };

  /* =========================================================================
     LISTEN FOR USER NOTIFICATION TOGGLES (real-time)
     - updates notifyRef so listeners can check permission without re-subscribing
     ========================================================================= */
  useEffect(() => {
    if (!userUid) return;

    const userQuery = query(
      collection(db, "users"),
      where("authUID", "==", userUid)
    );

    const unsub = onSnapshot(userQuery, (snapshot) => {
      console.log("USER SETTINGS SNAPSHOT FIRED");

      if (snapshot.empty) return;

      const userDoc = snapshot.docs[0].data();

      const allow =
        (isBrowser && userDoc.inBrowserNotifications !== false) ||
        (isApp && userDoc.appNotifications !== false);

      notifyRef.current = Boolean(allow);

      if (mounted.current) {
        setShowNotification(Boolean(allow));
      }
    });

    return () => unsub();
  }, [userUid, isBrowser, isApp]);

  // console.log("showNotification",showNotification)

  /* =========================================================================
     1. MESSAGE LISTENER (refined)
     - uses notifyRef to decide whether to show snackbars
     - cleans up removed threads and avoids duplicate listeners
     - protects against async callbacks after unmount
     ========================================================================= */
  useEffect(() => {
    if (!userUid) return;

    // Choose default lastSeenTime behavior: use stored value if present, otherwise 0
    // (this means first-time users will receive notifications for existing unread messages).
    // If you prefer the previous behavior (ignore old messages), change `|| 0` to `|| Date.now()`.
    const lsKey = LS_KEYS.MSG(userUid);
    let lastSeenTime = Number(localStorage.getItem(lsKey) || 0);

    const threadsQuery = query(
      collection(db, "messages"),
      where("participants", "array-contains", userUid),
      orderBy("updatedAt", "desc"),
      limit(20)
    );

    const unsubThreads = onSnapshot(threadsQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const threadId = change.doc.id;

        // If thread removed, cleanup its listener
        if (change.type === "removed") {
          if (threadListeners.current[threadId]) {
            try {
              threadListeners.current[threadId]();
            } catch (e) {
              /* ignore */
            }
            delete threadListeners.current[threadId];
          }
          return;
        }

        if (!["added", "modified"].includes(change.type)) return;

        // Avoid double-subscribing
        if (threadListeners.current[threadId]) return;

        const msgsQuery = query(
          collection(db, "messages", threadId, "msg"),
          orderBy("createdAt", "desc"),
          limit(11)
        );

        const unsubMsg = onSnapshot(msgsQuery, async (msgSnap) => {
          // Protect against running after component unmounted
          if (!mounted.current) return;

          const docs = msgSnap.docs;
          const unreadCount = docs.filter(
            (d) => !d.data().read && d.data().sender !== userUid
          ).length;

          const newMessages = [];

          // iterate docChanges to find newly added messages
          msgSnap.docChanges().forEach((mChange) => {
            if (mChange.type === "added") {
              const data = mChange.doc.data();
              const msgTime = data.createdAt?.seconds
                ? data.createdAt.seconds * 1000
                : Date.now();

              if (msgTime > lastSeenTime && data.sender !== userUid) {
                newMessages.push(data);
                lastSeenTime = Math.max(lastSeenTime, msgTime);
                try {
                  localStorage.setItem(lsKey, String(lastSeenTime));
                } catch (e) {
                  // localStorage may throw in some contexts — ignore safely
                }
              }
            }
          });

          if (newMessages.length > 0) {
            // Only show notification if notifyRef says so
            if (notifyRef.current) {
              try {
                const latestMsg = newMessages[0];
                const senderName = await getUserName(latestMsg.sender);

                let notificationText = "";
                if (unreadCount > 10) {
                  notificationText = `You have multiple messages from ${senderName}.`;
                } else if (unreadCount > 1) {
                  notificationText = `You have ${unreadCount} unread messages from ${senderName}.`;
                } else {
                  notificationText = `You have a new message from ${senderName}.`;
                }

                // final mount check before calling UI functions
                if (mounted.current) {
                  enqueueSnackbar(notificationText, {
                    variant: "message",
                  });
                }
              } catch (err) {
                console.error("message notification error:", err);
              }
            }
          }
        });

        // store unsub and continue
        threadListeners.current[threadId] = unsubMsg;
      });
    });

    return () => {
      unsubThreads();
      // cleanup all per-thread listeners
      Object.values(threadListeners.current).forEach((u) => {
        try {
          u();
        } catch (e) {
          /* ignore */
        }
      });
      threadListeners.current = {};
    };
    // NOTE: we intentionally do NOT include notifyRef or showNotification in deps,
    // because notifyRef is read synchronously inside listeners (avoids resubscribes).
  }, [userUid, enqueueSnackbar]);

  /* =========================================================================
     2. LIKES LISTENER (refined)
     ========================================================================= */
  useEffect(() => {
    if (!userUid) return;

    const lsKey = LS_KEYS.LIKE(userUid);
    let lastSeenTime = Number(localStorage.getItem(lsKey) || 0);

    const likesQuery = query(
      collection(db, "likes"),
      where("likedUid", "==", userUid),
      orderBy("createdAt", "desc"),
      limit(10)
    );

    const unsubLikes = onSnapshot(likesQuery, async (snapshot) => {
      if (!mounted.current) return;

      for (const change of snapshot.docChanges()) {
        if (change.type !== "added") continue;

        const docId = change.doc.id;
        const data = change.doc.data();

        // Ignore already-read notifications
        if (data.read === true) continue;

        const likeTime = data.createdAt?.seconds
          ? data.createdAt.seconds * 1000
          : Date.now();

        if (likeTime > lastSeenTime) {
          lastSeenTime = Math.max(lastSeenTime, likeTime);
          try {
            localStorage.setItem(lsKey, String(lastSeenTime));
          } catch (e) {
            /* ignore */
          }

          // Show notification only if allowed
          if (notifyRef.current) {
            try {
              const likerName = await getUserName(data.likerUid);
              if (mounted.current) {
                enqueueSnackbar(`${likerName} liked your account.`, {
                  variant: "like",
                });
              }
            } catch (err) {
              console.error("like notification error:", err);
            }
          }

          // Mark as read (defensive)
          try {
            await updateDoc(doc(db, "likes", docId), {
              read: true,
            });
          } catch (err) {
            console.error("Failed marking like read:", err);
          }
        }
      }
    });

    return () => unsubLikes();
  }, [userUid, enqueueSnackbar]);

  /* =========================================================================
     3. FAVORITES LISTENER (refined)
     ========================================================================= */
  useEffect(() => {
    if (!userUid) return;

    const lsKey = LS_KEYS.FAV(userUid);
    let lastSeenTime = Number(localStorage.getItem(lsKey) || 0);

    const favQuery = query(
      collection(db, "favourites"),
      where("favouritedUid", "==", userUid),
      orderBy("createdAt", "desc"),
      limit(10)
    );

    const unsubFav = onSnapshot(favQuery, async (snapshot) => {
      if (!mounted.current) return;

      for (const change of snapshot.docChanges()) {
        if (change.type !== "added") continue;

        const docId = change.doc.id;
        const data = change.doc.data();

        if (data.read === true) continue;

        const favTime = data.createdAt?.seconds
          ? data.createdAt.seconds * 1000
          : Date.now();

        if (favTime > lastSeenTime) {
          lastSeenTime = Math.max(lastSeenTime, favTime);
          try {
            localStorage.setItem(lsKey, String(lastSeenTime));
          } catch (e) {
            /* ignore */
          }

          if (notifyRef.current) {
            try {
              const fanName = await getUserName(data.favouriterUid);
              if (mounted.current) {
                enqueueSnackbar(`${fanName} added you to his favorites list.`, {
                  variant: "favorites",
                });
              }
            } catch (err) {
              console.error("favorite notification error:", err);
            }
          }

          try {
            await updateDoc(doc(db, "favourites", docId), {
              read: true,
            });
          } catch (err) {
            console.error("Failed marking favourite read:", err);
          }
        }
      }
    });

    return () => unsubFav();
  }, [userUid, enqueueSnackbar]);

  // expose anything you need (optional)
  return {
    showNotification, // boolean for UI
  };
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// import { useEffect, useRef, useState } from "react";
// import {
//   collection,
//   query,
//   where,
//   onSnapshot,
//   orderBy,
//   getDocs,
//   limit,
//   doc,
//   updateDoc,
// } from "firebase/firestore";
// import { db } from "../config/firebase";
// import { useSnackbar } from "notistack";

// /* =========================================================================
//    CONSTANTS
//    ========================================================================= */
// const LS_KEYS = {
//   MSG: (uid) => `last_seen_msg_timestamp_${uid}`,
//   LIKE: (uid) => `last_seen_like_timestamp_${uid}`,
//   FAV: (uid) => `last_seen_fav_timestamp_${uid}`,
// };

// export function useRealtimeNotifications({ userUid }) {
//   const { enqueueSnackbar } = useSnackbar();

//   const [showNotification, setShowNotification] = useState(false);

//   //for demo purpose
//   // const isBrowser = typeof window !== "undefined";
//   const isBrowser = true;
//   const isApp = false;

//   // REFS
//   const nameCache = useRef({});
//   const threadListeners = useRef({});

//   /* =========================================================================
//      HELPER: GET USER NAME
//      ========================================================================= */
//   const getUserName = async (uid) => {
//     if (!uid) return "Someone";
//     if (nameCache.current[uid]) return nameCache.current[uid];

//     try {
//       const userQuery = query(
//         collection(db, "users"),
//         where("authUID", "==", uid)
//       );

//       const snapshot = await getDocs(userQuery);

//       if (!snapshot.empty) {
//         const data = snapshot.docs[0].data();
//         const name = data.displayName || "Someone";
//         nameCache.current[uid] = name;
//         return name;
//       }
//     } catch (err) {
//       console.error("Name fetch error:", err);
//     }

//     return "Someone";
//   };

//   useEffect(() => {
//     if (!userUid) return;

//     const userDocRef = doc(db, "users", userUid);

//     const unsub = onSnapshot(userDocRef, (snapshot) => {
//       if (!snapshot.exists()) return;

//       const data = snapshot.data();
//       if (isBrowser) {
//         setShowNotification(data.inBrowserNotifications);
//       } else if (isApp) {
//         setShowNotification(data.appNotifications);
//       }
//     });

//     return () => unsub();
//   }, [userUid]);

//   /* =========================================================================
//      1. MESSAGE LISTENER (unchanged)
//      ========================================================================= */
//   useEffect(() => {
//     if (!userUid) return;

//     const lsKey = LS_KEYS.MSG(userUid);
//     let lastSeenTime = Number(localStorage.getItem(lsKey) || Date.now());

//     const threadsQuery = query(
//       collection(db, "messages"),
//       where("participants", "array-contains", userUid),
//       orderBy("updatedAt", "desc"),
//       limit(20)
//     );

//     const unsubThreads = onSnapshot(threadsQuery, (snapshot) => {
//       snapshot.docChanges().forEach((change) => {
//         const threadId = change.doc.id;

//         if (["added", "modified"].includes(change.type)) {
//           if (threadListeners.current[threadId]) return;

//           const msgsQuery = query(
//             collection(db, "messages", threadId, "msg"),
//             orderBy("createdAt", "desc"),
//             limit(11)
//           );

//           const unsubMsg = onSnapshot(msgsQuery, async (msgSnap) => {
//             const docs = msgSnap.docs;
//             const unreadCount = docs.filter(
//               (d) => !d.data().read && d.data().sender !== userUid
//             ).length;

//             const newMessages = [];

//             msgSnap.docChanges().forEach((mChange) => {
//               if (mChange.type === "added") {
//                 const data = mChange.doc.data();
//                 const msgTime = data.createdAt?.seconds
//                   ? data.createdAt.seconds * 1000
//                   : Date.now();

//                 if (msgTime > lastSeenTime && data.sender !== userUid) {
//                   newMessages.push(data);
//                   lastSeenTime = Math.max(lastSeenTime, msgTime);
//                   localStorage.setItem(lsKey, String(lastSeenTime));
//                 }
//               }
//             });

//             if (newMessages.length > 0) {
//               const latestMsg = newMessages[0];
//               const senderName = await getUserName(latestMsg.sender);

//               let notificationText = "";
//               if (unreadCount > 10) {
//                 notificationText = `You have multiple messages from ${senderName}.`;
//               } else if (unreadCount > 1) {
//                 notificationText = `You have ${unreadCount} unread messages from ${senderName}.`;
//               } else {
//                 notificationText = `You have a new message from ${senderName}.`;
//               }
//               if (showNotification) {
//                 enqueueSnackbar(notificationText, {
//                   variant: "message",
//                 });
//               }
//             }
//           });

//           threadListeners.current[threadId] = unsubMsg;
//         }
//       });
//     });

//     return () => {
//       unsubThreads();
//       Object.values(threadListeners.current).forEach((u) => u());
//       threadListeners.current = {};
//     };
//   }, [userUid, enqueueSnackbar]);

//   /* =========================================================================
//      2. LIKES LISTENER (with read field)
//      ========================================================================= */
//   useEffect(() => {
//     if (!userUid) return;

//     const lsKey = LS_KEYS.LIKE(userUid);
//     let lastSeenTime = Number(localStorage.getItem(lsKey) || Date.now());

//     const likesQuery = query(
//       collection(db, "likes"),
//       where("likedUid", "==", userUid),
//       orderBy("createdAt", "desc"),
//       limit(10)
//     );

//     const unsubLikes = onSnapshot(likesQuery, async (snapshot) => {
//       for (const change of snapshot.docChanges()) {
//         if (change.type !== "added") continue;

//         const docId = change.doc.id;
//         const data = change.doc.data();

//         // Ignore already-read notifications
//         if (data.read === true) continue;

//         const likeTime = data.createdAt?.seconds
//           ? data.createdAt.seconds * 1000
//           : Date.now();

//         if (likeTime > lastSeenTime) {
//           lastSeenTime = Math.max(lastSeenTime, likeTime);
//           localStorage.setItem(lsKey, String(lastSeenTime));

//           const likerName = await getUserName(data.likerUid);

//           if (showNotification) {
//             enqueueSnackbar(`${likerName} liked your account.`, {
//               variant: "like",
//             });
//           }
//           // Mark as read
//           await updateDoc(doc(db, "likes", docId), {
//             read: true,
//           });
//         }
//       }
//     });

//     return () => unsubLikes();
//   }, [userUid, enqueueSnackbar]);

//   /* =========================================================================
//      3. FAVORITES LISTENER (with read field)
//      ========================================================================= */
//   useEffect(() => {
//     if (!userUid) return;

//     const lsKey = LS_KEYS.FAV(userUid);
//     let lastSeenTime = Number(localStorage.getItem(lsKey) || Date.now());

//     const favQuery = query(
//       collection(db, "favourites"),
//       where("favouritedUid", "==", userUid),
//       orderBy("createdAt", "desc"),
//       limit(10)
//     );

//     const unsubFav = onSnapshot(favQuery, async (snapshot) => {
//       for (const change of snapshot.docChanges()) {
//         if (change.type !== "added") continue;

//         const docId = change.doc.id;
//         const data = change.doc.data();

//         if (data.read === true) continue;

//         const favTime = data.createdAt?.seconds
//           ? data.createdAt.seconds * 1000
//           : Date.now();

//         if (favTime > lastSeenTime) {
//           lastSeenTime = Math.max(lastSeenTime, favTime);
//           localStorage.setItem(lsKey, String(lastSeenTime));

//           const fanName = await getUserName(data.favouriterUid);

//           if (showNotification) {
//             enqueueSnackbar(`${fanName} added you to his favorites list.`, {
//               variant: "favorites",
//             });
//           }

//           // Mark as read
//           await updateDoc(doc(db, "favourites", docId), {
//             read: true,
//           });
//         }
//       }
//     });

//     return () => unsubFav();
//   }, [userUid, enqueueSnackbar]);
// }

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// import { useEffect, useRef } from "react";
// import {
//   collection,
//   query,
//   where,
//   onSnapshot,
//   orderBy,
//   getDocs, // Changed from getDoc
//   limit
// } from "firebase/firestore";
// import { db } from "../config/firebase";
// import { useSnackbar } from "notistack";

// /* =========================================================================
//    CONSTANTS
//    ========================================================================= */
// const LS_KEYS = {
//   MSG: (uid) => `last_seen_msg_timestamp_${uid}`,
//   LIKE: (uid) => `last_seen_like_timestamp_${uid}`,
//   FAV: (uid) => `last_seen_fav_timestamp_${uid}`,
// };

// export function useRealtimeNotifications({ userUid }) {
//   const { enqueueSnackbar } = useSnackbar();

//   // --- REFS (State that doesn't trigger re-renders) ---
//   const notifiedIds = useRef(new Set());
//   const nameCache = useRef({});
//   const threadListeners = useRef({});

//   // --- HELPER: RESOLVE USER NAME (FIXED) ---
//   const getUserName = async (uid) => {
//     if (!uid) return "Someone";

//     // 1. Check Cache first
//     if (nameCache.current[uid]) return nameCache.current[uid];

//     try {
//       // 2. Query 'users' collection where field 'authUID' matches the uid
//       const userQuery = query(
//         collection(db, "users"),
//         where("authUID", "==", uid)
//       );

//       const querySnapshot = await getDocs(userQuery);

//       if (!querySnapshot.empty) {
//         // Take the first matching document
//         const data = querySnapshot.docs[0].data();
//         const name = data.displayName || "Someone";

//         // 3. Save to cache
//         nameCache.current[uid] = name;
//         return name;
//       }
//     } catch (error) {
//       console.error("Error fetching name:", error);
//     }
//     return "Someone";
//   };

//   /* =========================================================================
//      1. MESSAGE LISTENER
//      ========================================================================= */
//   useEffect(() => {
//     if (!userUid) return;

//     const lsKey = LS_KEYS.MSG(userUid);
//     let lastSeenTime = Number(localStorage.getItem(lsKey) || Date.now());

//     const threadsQuery = query(
//       collection(db, "messages"),
//       where("participants", "array-contains", userUid),
//       orderBy("updatedAt", "desc"),
//       limit(20)
//     );

//     const unsubThreads = onSnapshot(threadsQuery, (snapshot) => {
//       snapshot.docChanges().forEach((change) => {
//         const threadId = change.doc.id;

//         if (["added", "modified"].includes(change.type)) {
//           if (threadListeners.current[threadId]) return;

//           const msgsQuery = query(
//             collection(db, "messages", threadId, "msg"),
//             orderBy("createdAt", "desc"),
//             limit(10)
//           );

//           const unsubMsg = onSnapshot(msgsQuery, async (msgSnap) => {
//             const docs = msgSnap.docs;
//             // Count unread messages from OTHERS
//             const unreadCount = docs.filter(
//               (d) => !d.data().read && d.data().sender !== userUid
//             ).length;

//             const newMessages = [];

//             msgSnap.docChanges().forEach((mChange) => {
//               if (mChange.type === "added") {
//                 const data = mChange.doc.data();
//                 // Safety check for timestamps (handle nulls if serverTimestamp hasn't resolved)
//                 const msgTime = data.createdAt?.seconds ? data.createdAt.seconds * 1000 : Date.now();

//                 if (
//                   msgTime > lastSeenTime &&
//                   data.sender !== userUid &&
//                   !notifiedIds.current.has(mChange.doc.id)
//                 ) {
//                   newMessages.push(data);
//                   notifiedIds.current.add(mChange.doc.id);
//                   lastSeenTime = Math.max(lastSeenTime, msgTime);
//                   localStorage.setItem(lsKey, String(lastSeenTime));
//                 }
//               }
//             });

//             if (newMessages.length > 0) {
//               const latestMsg = newMessages[0];
//               const senderName = await getUserName(latestMsg.sender);

//               let notificationText = "";
//               if (unreadCount > 1) {
//                 notificationText = `You have ${unreadCount} unread messages from ${senderName}.`;
//               } else {
//                 notificationText = `You have a new message from ${senderName}.`;
//               }

//               enqueueSnackbar(notificationText, {
//                 variant: "message",

//               });
//             }
//           });

//           threadListeners.current[threadId] = unsubMsg;
//         }
//       });
//     });

//     return () => {
//       unsubThreads();
//       Object.values(threadListeners.current).forEach((u) => u());
//       threadListeners.current = {};
//     };
//   }, [userUid, enqueueSnackbar]);

//   /* =========================================================================
//      2. LIKES LISTENER
//      ========================================================================= */
//   useEffect(() => {
//     if (!userUid) return;

//     const lsKey = LS_KEYS.LIKE(userUid);
//     let lastSeenTime = Number(localStorage.getItem(lsKey) || Date.now());

//     const likesQuery = query(
//       collection(db, "likes"),
//       where("likedUid", "==", userUid),
//       orderBy("createdAt", "desc"),
//       limit(10)
//     );

//     const unsubLikes = onSnapshot(likesQuery, async (snapshot) => {
//       for (const change of snapshot.docChanges()) {
//         if (change.type === "added") {
//           const data = change.doc.data();
//           const likeTime = data.createdAt?.seconds ? data.createdAt.seconds * 1000 : Date.now();

//           if (likeTime > lastSeenTime && !notifiedIds.current.has(change.doc.id)) {
//             notifiedIds.current.add(change.doc.id);
//             lastSeenTime = Math.max(lastSeenTime, likeTime);
//             localStorage.setItem(lsKey, String(lastSeenTime));

//             const likerName = await getUserName(data.likerUid);

//             enqueueSnackbar(`${likerName} liked your account.`, {
//               variant: "like",

//             });
//           }
//         }
//       }
//     });

//     return () => unsubLikes();
//   }, [userUid, enqueueSnackbar]);

//   /* =========================================================================
//      3. FAVORITES LISTENER
//      ========================================================================= */
//   useEffect(() => {
//     if (!userUid) return;

//     const lsKey = LS_KEYS.FAV(userUid);
//     let lastSeenTime = Number(localStorage.getItem(lsKey) || Date.now());

//     const favQuery = query(
//       collection(db, "favourites"),
//       where("favouritedUid", "==", userUid),
//       orderBy("createdAt", "desc"),
//       limit(10)
//     );

//     const unsubFav = onSnapshot(favQuery, async (snapshot) => {
//       for (const change of snapshot.docChanges()) {
//         if (change.type === "added") {
//           const data = change.doc.data();
//           const favTime = data.createdAt?.seconds ? data.createdAt.seconds * 1000 : Date.now();

//           if (favTime > lastSeenTime && !notifiedIds.current.has(change.doc.id)) {
//             notifiedIds.current.add(change.doc.id);
//             lastSeenTime = Math.max(lastSeenTime, favTime);
//             localStorage.setItem(lsKey, String(lastSeenTime));

//             const fanName = await getUserName(data.favouriterUid);

//             enqueueSnackbar(`${fanName} added you to his favorites list.`, {
//               variant: "favorites", // Using 'message' as requested in reference

//             });
//           }
//         }
//       }
//     });

//     return () => unsubFav();
//   }, [userUid, enqueueSnackbar]);
// }
